import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { useApplicationContext } from "../../application/ApplicationContext";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { ProgressSpinner } from "primereact/progressspinner";
import { RoleType } from "../../models/Role";
import { ProcessingOrderListItem } from "../../models/ProcessingOrder";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { activeUser } from "../../services/AuthService";
import { Utility } from "../../core/Utility";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useTranslation } from "react-i18next";
import { AxiosResponse, AxiosError } from "axios";
import {
  deleteOneProcessingOrder,
  getProcessingOrders,
} from "../../services/FinishingCenterService";
import "./ProcessingOrdersPage.scss";

export const ProcessingOrdersPage = () => {
  const {
    //load and set global filters
    state,
    selectedDateFromSet,
    selectedDateToSet,
  } = useApplicationContext();

  const { t } = useTranslation();
  const toast = useRef(null);
  const navigate = useNavigate();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const today = new Date();
  const isCurrentBusinessYear = businessYear === today.getFullYear();
  const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31, 23, 59, 59);
  const initialDateFrom = isCurrentBusinessYear
    ? new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 30,
        0,
        0,
        1,
      )
    : new Date(businessYear, 0, 1, 0, 0, 1); // 1.1. poslovne godine

  const initialDateTo = isCurrentBusinessYear
    ? today
    : new Date(businessYear, 11, 31, 23, 59, 59); // 31.12. poslovne godine
  const [dateFrom, setDateFrom] = useState<Date>(
    state.selectedDateFrom ?? initialDateFrom,
  );
  const [dateTo, setDateTo] = useState<Date>(
    state.selectedDateTo ?? initialDateTo,
  );

  const [allProcessingOrders, setAllProcessingOrders] = useState<
    ProcessingOrderListItem[]
  >([]);
  const [filteredInputDocuments, setFilteredInputDocuments] = useState<
    ProcessingOrderListItem[]
  >([]);

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [inputToDelete, setInputToDelete] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState({
    plantType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "sort.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
    number: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "request.number": { value: null, matchMode: FilterMatchMode.CONTAINS },
    totalAmountKg: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  useEffect(() => {
    setProcessing(true);
    if (state.selectedDateFrom) setDateFrom(state?.selectedDateFrom);
    if (state.selectedDateTo) setDateTo(state?.selectedDateTo);

    const effectiveFrom = state?.selectedDateFrom ?? initialDateFrom;
    const effectiveTo = state?.selectedDateTo ?? initialDateTo;

    getProcessingOrders(effectiveFrom, effectiveTo)
      .then((response: AxiosResponse) => {
        const allNewOrders = response.data.result;
        setAllProcessingOrders(allNewOrders);
      })
      .catch((error: AxiosError) => {
        console.log(error);
      })
      .finally(() => setProcessing(false));
  }, []);

  useEffect(() => {
    const today = new Date();
    const isCurrent = businessYear === today.getFullYear();

    const newFrom = isCurrent
      ? new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - 30,
          0,
          0,
          1,
        )
      : new Date(businessYear, 0, 1, 0, 0, 1);

    const newTo = isCurrent
      ? today
      : new Date(businessYear, 11, 31, 23, 59, 59);

    setDateFrom(newFrom);
    setDateTo(newTo);
    selectedDateFromSet(newFrom);
    selectedDateToSet(newTo);
  }, [businessYear]);

  const resetFilters = () => {
    setFilters({
      plantType: { value: null, matchMode: FilterMatchMode.CONTAINS },
      "sort.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
      number: { value: null, matchMode: FilterMatchMode.CONTAINS },
      "request.number": { value: null, matchMode: FilterMatchMode.CONTAINS },
      totalAmountKg: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  useEffect(() => {
    setProcessing(true);
    const effectiveFrom = state?.selectedDateFrom ?? initialDateFrom;
    const effectiveTo = state?.selectedDateTo ?? initialDateTo;

    getProcessingOrders(effectiveFrom, effectiveTo)
      .then((response: AxiosResponse) => {
        const allOrders = response.data.result;
        setAllProcessingOrders(allOrders);
        resetFilters();
      })
      .finally(() => setProcessing(false));
  }, [state.selectedDateFrom, state.selectedDateTo]);

  const onDateFromChange = (property: string, value: Date | null) => {
    setDateFrom(value as Date);
    selectedDateFromSet(value as Date);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
    selectedDateToSet(value as Date);
  };

  const accept = () => {
    setProcessing(true);
    deleteOneProcessingOrder(inputToDelete)
      .then(() => {
        setInputToDelete("");
      })
      .then(() => {
        const effectiveFrom = state?.selectedDateFrom ?? initialDateFrom;
        const effectiveTo = state?.selectedDateTo ?? initialDateTo;

        getProcessingOrders(effectiveFrom, effectiveTo).then(
          (response: AxiosResponse) => {
            const allOrders = response.data.result;
            setAllProcessingOrders(allOrders);
            resetFilters();
          },
        );
      })
      .finally(() => setProcessing(false));
    //@ts-ignore
    toast.current.show({
      severity: "success",
      detail: t(`Nalog je izbrisan`),
      life: 3000,
    });
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const formatDate = (rowData: ProcessingOrderListItem) => {
    return Utility.formatDate(new Date(rowData?.date as Date));
  };

  const formatProcessingCenter = (rowData: ProcessingOrderListItem) => {
    return rowData?.processingCenter?.name ?? "";
  };

  const formatRequestNumber = (rowData: ProcessingOrderListItem) => {
    return rowData?.request?.number ?? "";
  };

  const formatAmountKg = (rowData: ProcessingOrderListItem) => {
    return Math.round(parseInt(rowData?.totalAmountKg?.toString() ?? "")) ?? "";
  };

  const formatSort = (rowData: ProcessingOrderListItem) => {
    if (!rowData?.sort) return "";
    return `${rowData.sort.name ?? ""}`.trim();
  };

  const formatOptions = (rowData: ProcessingOrderListItem) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            const id = rowData.id;
            navigate(`/processing-orders/${id}`);
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Director) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-trash"
            onButtonClick={async () => {
              const id = rowData.id;
              setInputToDelete(id);
              setDeleteConfirmVisible(true);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
      </div>
    );
  };

  const exportToExcel = () => {
    const sortedOrders = [...filteredInputDocuments].sort((a, b) => {
      const dateA = new Date(a.date as Date).getTime();
      const dateB = new Date(b.date as Date).getTime();
      return dateA - dateB;
    });

    const excelOrdersObject = sortedOrders.map(
      (item: ProcessingOrderListItem) => {
        return {
          BrojNaloga: item.number,
          Datum: Utility.formatDate(new Date(item.date as Date)),
          BrojPrijemnogZahteva: item.requestNumber,
          DoradniCentar: item.processingCenter?.name,
          BiljnaVrsta: item.plantType,
          Sorta: item.sort ? `${item.sort.name ?? ""}`.trim() : "",
          KategorijaSemena: item.seedCategory,
          VrstaPakovanja: item.packingType,
          BrojPakovanja: item.packageCount,
          UkupnaKolicina: item.totalAmountKg,
          Tretman: item.treatment,
          Atest: item.atestType,
        };
      },
    );
    const excelObject = [
      {
        BrojNaloga: "",
        Datum: "",
        BrojPrijemnogZahteva: "",
        DoradniCentar: "",
        BiljnaVrsta: "",
        Sorta: "",
        KategorijaSemena: "",
        VrstaPakovanja: "",
        BrojPakovanja: "",
        UkupnaKolicina: "",
        Tretman: "",
        Atest: "",
      },
      ...excelOrdersObject,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Zahtevi za doradu i pakovanje ${Utility.formatDate(new Date())}`,
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">
              {t("Nalozi za doradu i pakovanje")}
            </span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const today = new Date();
                const isCurrent = businessYear === today.getFullYear();
                const newFrom = isCurrent
                  ? new Date(
                      today.getFullYear(),
                      today.getMonth(),
                      today.getDate() - 30,
                      0,
                      0,
                      1,
                    )
                  : new Date(businessYear, 0, 1, 0, 0, 1);
                const newTo = isCurrent
                  ? today
                  : new Date(businessYear, 11, 31, 23, 59, 59);
                // lokalni state
                setDateFrom(newFrom);
                setDateTo(newTo);
                // globalni filteri
                selectedDateFromSet(newFrom);
                selectedDateToSet(newTo);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem",
              }}
              labelText={t("Novi nalog")}
              icon="bi bi-box"
              onButtonClick={() => {
                navigate(`/processing-orders/new`);
              }}
              disabled={processing === true}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              emptyMessage="Nema naloga za doradu i pakovanje"
              value={allProcessingOrders}
              onValueChange={(filteredData) =>
                setFilteredInputDocuments(filteredData)
              }
              showGridlines
              stripedRows
            >
              <Column
                field="number"
                filter
                header={t("Broj naloga")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
              />
              <Column
                body={formatRequestNumber}
                header={t("Prijemni zahtev")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={formatProcessingCenter}
                header={t("Doradni centar")}
                style={{ minWidth: "90px" }}
              />
              <Column
                field="plantType"
                sortable
                filter
                header={t("Biljna vrsta")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="sort.name"
                body={formatSort}
                sortable
                filter
                header={t("Sorta")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatAmountKg}
                field="totalAmountKg"
                filter
                style={{ minWidth: "80px" }}
                header={t("Količina")}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "150px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t(
          "Da li ste sigurni da želite da obrišete nalog za doradu i pakovanje?",
        )}
        header={t("Potvrda brisanja naloga za doradu i pakovanje")}
        icon="bi bi-exclamation-circle"
        acceptLabel={`${t("Da")}`}
        acceptClassName="accept-button"
        accept={accept}
        rejectLabel={`${t("Ne")}`}
        rejectClassName="reject-button"
        reject={() => {}}
      />
    </PageLayout>
  );
};
